/* @flow */

import classNames from 'classnames';
import * as React from 'react';
import { compose } from 'ramda';
import { useTheme } from 'react-jss';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import styles from './Button.style';

import withForwardRef from '@braindate/util/lib/react/hoc/withForwardRef';

import BaseButton from 'src/shared/ui/component/button/BaseButton';

import type { Button as ButtonType } from '@braindate/ui/lib/component/button/type/buttonTypes';
import Tooltip from '@mui/material/Tooltip';
import ConditionalWrapper from 'src/shared/ui/component/misc/ConditionalWrapper';

export const VARIANT: { PRIMARY: 'primary', SECONDARY: 'secondary' } = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
};

export type Props = ButtonType & {
  fitText?: ?boolean,
  stopPropagation?: ?boolean,
  withSuccess?: ?boolean,
  withEvenSide?: ?boolean,
  onClick?: ?(Event) => void,
  onMouseOver?: ?(Event) => void,
  withEvenSides?: ?boolean,
  color?: string,
  variant?: 'primary' | 'secondary',
  tooltip?: string,
  'aria-disabled'?: boolean,
};

const useStyles = createUseThemeStyles(styles);

const Button = ({
  layoutClass,
  href,
  onClick,
  onMouseOver,
  isDisabled,
  isSubmitting,
  isFetching,
  large,
  fitText,
  stopPropagation,
  withSuccess,
  hasFocus,
  children,
  id,
  withEvenSides,
  variant = VARIANT.SECONDARY,
  color,
  tooltip,
  'aria-disabled': ariaDisabled,
  ...props
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */
  const { accentColor } = useTheme();

  const classes = useStyles({
    withSuccess,
    isPrimary: variant === VARIANT.PRIMARY,
    customColor: color ? color : accentColor,
    ariaDisabled,
  });

  const rootClasses = classNames(classes.root, layoutClass);

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <ConditionalWrapper
      condition={!!tooltip}
      wrapper={(children) => (
        <Tooltip title={tooltip} arrow placement="top">
          {children}
        </Tooltip>
      )}
    >
      <BaseButton
        {...props}
        className={rootClasses}
        href={href}
        onClick={onClick}
        onMouseOver={onMouseOver}
        isDisabled={isDisabled}
        aria-disabled={ariaDisabled}
        isSubmitting={isSubmitting}
        isFetching={isFetching}
        large={large}
        fitText={fitText}
        stopPropagation={stopPropagation}
        withSuccess={withSuccess}
        hasFocus={hasFocus}
        id={id}
        withEvenSides={withEvenSides}
      >
        {children}
      </BaseButton>
    </ConditionalWrapper>
  );
};

export default compose(React.memo, withForwardRef)(Button);
